import { Head, Link } from "@inertiajs/react";
import Breadcrumb from "@/shared/Breadcrumb";
import LikeButton from "@/shared/likes/LikeButton";
import SponsorSlot from "@/shared/sponsors/SponsorSlot";
import { SPONSOR_DESKTOP_ONLY, SPONSOR_MOBILE_ONLY } from "@/shared/sponsors/visibility";
import { Text, Flex, Box, Button, Callout, Container, Heading, Section } from "@radix-ui/themes";
import MediaBrowser from "@/shared/media/MediaBrowser";
import SelfReportsSection from "@/shared/events/SelfReportsSection";
import ApplyCard from "@/shared/events/ApplyCard";
import EventPoster from "@/shared/events/EventPoster";
import { useBrand } from "@/shared/brand/useBrand";
import type { EventsShowEvent, EventsShowProps, EventsShowVenue } from "@/types/events_show";
import {
  AtSign,
  CalendarDays,
  Camera,
  Clock,
  ExternalLink,
  Eye,
  Facebook,
  Globe,
  Info,
  Instagram,
  MapPin,
  Music2,
  Twitter,
  Youtube,
} from "lucide-react";

function parseSocialUrls(raw: string | null): Array<{
  key: string;
  href: string;
  label: string;
  icon: React.ReactNode;
}> {
  if (!raw) return [];
  const ICON = 16;
  const seen = new Set<string>();
  const results: Array<{ key: string; href: string; label: string; icon: React.ReactNode }> = [];

  raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((url) => {
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      if (seen.has(href)) return;
      seen.add(href);

      let host = "";
      try {
        host = new URL(href).hostname.replace(/^www\./, "").toLowerCase();
      } catch {
        results.push({ key: href, href, label: "Website", icon: <Globe size={ICON} /> });
        return;
      }

      if (host.endsWith("instagram.com"))
        results.push({ key: href, href, label: "Instagram", icon: <Instagram size={ICON} /> });
      else if (host.endsWith("youtube.com") || host === "youtu.be")
        results.push({ key: href, href, label: "YouTube", icon: <Youtube size={ICON} /> });
      else if (host.endsWith("facebook.com") || host.endsWith("fb.com") || host === "fb.me")
        results.push({ key: href, href, label: "Facebook", icon: <Facebook size={ICON} /> });
      else if (host.endsWith("tiktok.com"))
        results.push({ key: href, href, label: "TikTok", icon: <Music2 size={ICON} /> });
      else if (host.endsWith("threads.net") || host.endsWith("threads.com"))
        results.push({ key: href, href, label: "Threads", icon: <AtSign size={ICON} /> });
      else if (host.endsWith("twitter.com") || host === "x.com")
        results.push({ key: href, href, label: "X / Twitter", icon: <Twitter size={ICON} /> });
      else if (host.endsWith("flickr.com"))
        results.push({ key: href, href, label: "Flickr", icon: <Camera size={ICON} /> });
      else
        results.push({ key: href, href, label: host, icon: <Globe size={ICON} /> });
    });

  return results;
}

function VenueRichSection({ title, html }: { title: string; html: string }) {
  return (
    <Box>
      <Heading size="4" mb="2">{title}</Heading>
      <div className="trix-content" dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
}

function EventVenueSections({ venue, trackName }: { venue: EventsShowVenue; trackName: string }) {
  const hasAny =
    venue.parking_html ||
    venue.pit_access_html ||
    venue.connectivity_html ||
    venue.transportation_html ||
    venue.accessibility_html ||
    venue.family_info_html ||
    venue.prohibited_items_html ||
    venue.rules ||
    venue.venue_map_url;

  if (!hasAny) return null;

  return (
    <Box mt="8">
      <Heading size="7" mb="4">What to Expect at {trackName}</Heading>
      <Flex direction="column" gap="5">
        {venue.venue_map_url && (
          <Box>
            <Heading size="4" mb="2">Venue Map</Heading>
            <img
              src={venue.venue_map_url}
              alt={`${trackName} venue map`}
              style={{ maxWidth: "100%", borderRadius: 6 }}
            />
          </Box>
        )}
        {venue.parking_html && <VenueRichSection title="Parking" html={venue.parking_html} />}
        {venue.pit_access_html && <VenueRichSection title="Pit Access" html={venue.pit_access_html} />}
        {venue.connectivity_html && <VenueRichSection title="Connectivity" html={venue.connectivity_html} />}
        {venue.transportation_html && <VenueRichSection title="Transportation" html={venue.transportation_html} />}
        {venue.accessibility_html && <VenueRichSection title="Accessibility" html={venue.accessibility_html} />}
        {venue.family_info_html && <VenueRichSection title="Family Info" html={venue.family_info_html} />}
        {venue.prohibited_items_html && <VenueRichSection title="Prohibited Items" html={venue.prohibited_items_html} />}
        {venue.rules && (
          <Box>
            <Heading size="4" mb="2">Rules</Heading>
            <Text as="p" className="whitespace-pre-line">{venue.rules}</Text>
          </Box>
        )}
      </Flex>
    </Box>
  );
}

/** One label/value line of "Need to know". */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-gray4 pb-3 last:border-b-0 last:pb-0 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
      <Text weight="bold" size="3">{label}</Text>
      <div>{children}</div>
    </div>
  );
}

function NeedToKnow({ event }: { event: EventsShowEvent }) {
  return (
    <Flex direction="column" gap="4">
      <Heading as="h2" size="6">Need to know</Heading>
      {event.description && (
        <Fact label="What is it?">
          <div className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: event.description.trim() }} />
        </Fact>
      )}
      {event.price && (
        <Fact label="Pricing">
          <Text as="div" dangerouslySetInnerHTML={{ __html: event.price }} />
        </Fact>
      )}
      <Fact label="Date">
        <Text>{event.date}</Text>
      </Fact>
      {event.start_time && (
        <Fact label="Start">
          <Text>{event.start_time}</Text>
        </Fact>
      )}
      {event.end_time && (
        <Fact label="End">
          <Text>{event.end_time}</Text>
        </Fact>
      )}
      {event.has_schedule && event.schedule_html && (
        <Fact label="Schedule">
          <Text as="div" dangerouslySetInnerHTML={{ __html: event.schedule_html }} />
        </Fact>
      )}
    </Flex>
  );
}

/** "Hosted by [club] at [track]", or just the track. Plain text on a branded
 *  site, which has no track or club pages to link to. */
function HostLine({ event }: { event: EventsShowEvent }) {
  const brand = useBrand();
  const track = brand ? (
    event.track.name
  ) : (
    <Link href={`/tracks/${event.track.slug}`} className="text-accent11 hover:underline">
      {event.track.name}
    </Link>
  );
  const club =
    event.club &&
    (brand ? (
      event.club.name
    ) : (
      <Link href={event.club.path} className="text-accent11 hover:underline">
        {event.club.name}
      </Link>
    ));
  return (
    <Text size="2" color="gray">
      {club ? (
        <>
          Hosted by {club} at {track}
        </>
      ) : (
        track
      )}
    </Text>
  );
}

function EventHeader({ event, like }: Pick<EventsShowProps, "event" | "like">) {
  const brand = useBrand();
  const socials = parseSocialUrls(event.social_urls);
  const times = [event.start_time, event.end_time].filter(Boolean).join(" – ");

  return (
    <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
      <Flex direction="column" gap="3" className="min-w-0">
        {/* A branded site has no breadcrumbs (they climb through tracks), so
            it gets the way back to its events instead. */}
        {brand ? (
          <Text size="2">
            <Link href="/events" className="text-accent11 hover:underline">
              All events
            </Link>
          </Text>
        ) : (
          <Breadcrumb
            crumbs={[
              { label: "Tracks", href: "/tracks" },
              { label: event.track.name, href: `/tracks/${event.track.slug}` },
              { label: event.name },
            ]}
          />
        )}
        {/* Hidden on mobile, where the poster header carries the name */}
        <div className="hidden sm:block">
          <Heading size="8" className="text-xl md:text-2xl lg:text-4xl">{event.name}</Heading>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={14} className="text-gray9" />
            <Text size="2" color="gray">{event.date}</Text>
          </span>
          {times && (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} className="text-gray9" />
              <Text size="2" color="gray">{times}</Text>
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={14} className="text-gray9" />
            <HostLine event={event} />
          </span>
        </div>
        {(!event.is_past || socials.length > 0) && (
          <Flex gap="3" align="center" wrap="wrap" mt="1">
            {!event.is_past && <LikeButton likeable={event} type="Event" like={like} />}
            {socials.map(({ key, href, label, icon }) => (
              <Button key={key} asChild variant="soft" color="gray" size="2">
                <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                  {icon}
                </a>
              </Button>
            ))}
          </Flex>
        )}
      </Flex>
      <div className="hidden h-52 w-80 flex-none overflow-hidden rounded-lg sm:block">
        <EventPoster name={event.name} date={event.date} url={event.poster_url} fit="contain" />
      </div>
    </div>
  );
}

export default function EventShow({
  event,
  like,
  participants,
  applications,
  approved_media,
  self_reports,
  sponsor_leaderboard,
  sponsor_sidebar,
  sponsor_mobile_banner,
}: EventsShowProps) {
  // The day of the event, and after, there's nothing left to apply to.
  const upcoming = !event.is_past_or_today;
  // A branded site carries no Driftables sponsors; the server sends no
  // creative (Sponsorable), and this drops the slots' spacing too.
  const sponsored = !useBrand();

  return (
    <>
      <Head title={event.name} />

      {/* Mobile poster header */}
      <div className="sm:hidden">
        <EventPoster name={event.name} date={event.date} url={event.poster_url} height={event.poster_url ? undefined : 220} size="lg" />
      </div>

      <Section size="1" px="4">
        <Container size="4">
          <EventHeader event={event} like={like} />

          {sponsored && (
            <>
              <div className={`${SPONSOR_DESKTOP_ONLY} mt-6 text-center`}>
                <SponsorSlot {...sponsor_leaderboard} />
              </div>
              <div className={`${SPONSOR_MOBILE_ONLY} mt-6 text-center`}>
                <SponsorSlot {...sponsor_mobile_banner} />
              </div>
            </>
          )}

          {event.notes_html && (
            <Callout.Root color="amber" variant="surface" mt="6">
              <Callout.Icon>
                <Info size={16} />
              </Callout.Icon>
              {/* Not Callout.Text: it always renders a <p>, and rich text has block elements. */}
              <Text as="div" size="2" className="trix-content" dangerouslySetInnerHTML={{ __html: event.notes_html }} />
            </Callout.Root>
          )}

          {!event.is_past && (
            <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-start">
              <div className="min-w-0 rounded-xl border border-gray5 bg-panel p-6 lg:flex-[3]">
                <NeedToKnow event={event} />
              </div>

              <div className="flex flex-col gap-4 lg:flex-[2]">
                {upcoming && (
                  <ApplyCard event={event} participants={participants} applications={applications} />
                )}

                {upcoming && event.track.signup_url && (
                    <Text size="2">
                      <a
                        href={event.track.signup_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-accent11 hover:underline"
                      >
                        <ExternalLink size={14} />
                        Sign up on {event.track.name}'s site
                      </a>
                    </Text>
                )}

                <Callout.Root color="blue" variant="soft">
                  <Callout.Icon>
                    <Eye size={16} />
                  </Callout.Icon>
                  <Callout.Text>
                    <Text weight="bold">Spectators are always welcome.</Text> Just show up on the day — no
                    registration needed. Check the pricing section for gate admission details.
                  </Callout.Text>
                </Callout.Root>

                {sponsored && (
                  <div className={`${SPONSOR_DESKTOP_ONLY} text-center`}>
                    <SponsorSlot {...sponsor_sidebar} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Past events have no right column; the sidebar slot still runs. */}
          {event.is_past && sponsored && (
            <div className={`${SPONSOR_DESKTOP_ONLY} mt-8 text-center`}>
              <SponsorSlot {...sponsor_sidebar} />
            </div>
          )}

          {/* Approved media applicants — the track said yes, so unlike the
              self reports below this needs no caveat. */}
          {approved_media.length > 0 && (
            <Box mt="8">
              <Heading size="7" mb="4">
                {event.is_past_or_today ? "Which media was there" : "Which media will be there"}
              </Heading>
              <MediaBrowser groups={[{ key: "approved", items: approved_media }]} />
            </Box>
          )}

          {self_reports && <SelfReportsSection eventId={event.id} selfReports={self_reports} />}

          {!event.is_past && <EventVenueSections venue={event.venue} trackName={event.track.name} />}
        </Container>
      </Section>
    </>
  );
}
